import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { extractLogoColors } from "@/components/features/company-image/logoColorExtraction";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Fiscalo ink — the backend's FISCALO_DEFAULT_BRAND_COLOR. This is what an
 * invoice renders with when no colour is set, so it is also the picker's
 * fallback. (The old value here was #0f2a44, from the pre-v1.0 navy palette
 * that no longer exists anywhere else.)
 */
const FISCALO_DEFAULT_BRAND_COLOR = "#161514";

/** WCAG relative luminance. */
function luminance(hex: string): number | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const [r, g, b] = (m[1].match(/../g) ?? []).map((h) => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Contrast of WHITE text on the given colour.
 *
 * The renderer paints the fiscal-grid labels and the line-item table header
 * with `fillColor('#ffffff')` on top of this colour, so a light brand colour
 * makes those headers unreadable on every invoice the tenant issues. We warn
 * rather than block: it is the user's brand, and the threshold is advisory.
 *
 * The card already offers "Vista previa de factura", which renders the real
 * PDF — a second, approximate mini-preview here was redundant and made the
 * panel busy, so only this check survives from it.
 */
export function whiteTextContrast(hex: string): number | null {
  const l = luminance(hex);
  if (l == null) return null;
  return 1.05 / (l + 0.05);
}

export interface InvoiceBrandColorSectionProps {
  /** Logo to mine for colour suggestions; null disables the suggestion row. */
  logoUrl: string | null;
  savedColor: string | null;
  saving: boolean;
  disabled?: boolean;
  onSave: (brandColor: string | null) => void;
  /**
   * Render without the card chrome and without the internal heading, and put
   * the actions in a right-aligned footer.
   *
   * For the dialog: there the title and the explanation are the dialog's own,
   * so the panel repeating them reads as the same sentence twice, and a save
   * button floating mid-card reads as part of the content rather than as the
   * thing that commits it.
   */
  chromeless?: boolean;
}

export function InvoiceBrandColorSection({
  logoUrl,
  savedColor,
  saving,
  disabled,
  onSave,
  chromeless = false,
}: InvoiceBrandColorSectionProps): JSX.Element {
  const { t } = useTranslation();
  // Seeded with the EFFECTIVE colour, never empty. Showing an empty field when
  // no colour is saved surfaced the placeholder ("ej. #000000", clipped by the
  // narrow input) and read as broken — the invoice does have a colour, it is
  // just the Fiscalo default.
  const [hexInput, setHexInput] = useState(savedColor ?? FISCALO_DEFAULT_BRAND_COLOR);
  const [logoColors, setLogoColors] = useState<string[]>([]);

  useEffect(() => {
    setHexInput(savedColor ?? FISCALO_DEFAULT_BRAND_COLOR);
  }, [savedColor]);

  // Colour suggestions taken from the tenant's own logo. Best-effort: on any
  // failure the list stays empty and only the fixed palette shows.
  useEffect(() => {
    let cancelled = false;
    if (!logoUrl) {
      setLogoColors([]);
      return;
    }
    void extractLogoColors(logoUrl).then((colors) => {
      if (!cancelled) setLogoColors(colors);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  const isValidHex = (s: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(s.trim());
  const normalizeHexInput = (raw: string): string => {
    const cleaned = raw.replace(/[^#0-9A-Fa-f]/gi, "");
    const digits = cleaned.replace(/#/g, "").slice(0, 6);
    return digits.length > 0 ? `#${digits}` : "";
  };

  const trimmed = hexInput.trim();
  const pendingHex = trimmed === "" ? null : isValidHex(trimmed) ? trimmed : null;
  const showFormatError = trimmed.length > 0 && !isValidHex(trimmed);
  const savedOrNull = savedColor ?? null;

  /** What the PDF will actually paint with, including the no-colour fallback. */
  const effectiveHex = pendingHex ?? savedOrNull ?? FISCALO_DEFAULT_BRAND_COLOR;
  const contrast = whiteTextContrast(effectiveHex);
  const lowContrast = contrast != null && contrast < 4.5;

  const canSave =
    pendingHex !== null &&
    pendingHex.toLowerCase() !==
      (savedOrNull ?? FISCALO_DEFAULT_BRAND_COLOR).toLowerCase();

  const saveButton = (
    <Button
      type="button"
      size="sm"
      onClick={() => onSave(pendingHex)}
      disabled={Boolean(disabled) || saving || !canSave}
      className="transition-colors duration-150"
    >
      {saving ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t("settings.brandColor.saving")}
        </>
      ) : (
        t("settings.brandColor.saveButton")
      )}
    </Button>
  );

  const useDefaultLink =
    savedColor != null ? (
      <button
        type="button"
        // Saves directly rather than clearing the input. Clearing it was
        // silently undone: clicking this blurs the hex field, and the blur
        // handler restores the saved value for any empty/invalid entry — so
        // the button appeared to do nothing at all.
        onClick={() => onSave(null)}
        disabled={disabled || saving}
        className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
      >
        {t("settings.brandColor.useDefault")}
      </button>
    ) : null;

  return (
    <div className={chromeless ? "space-y-3" : "space-y-3 rounded-lg border p-4"}>
      {!chromeless && (
        <div>
          <Label className="text-sm font-semibold">{t("settings.brandColor.title")}</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("settings.brandColor.descriptionWhere")}
          </p>
        </div>
      )}

      {/* ---- Picker row ---- */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="space-y-1">
          <Input
            type="text"
            value={hexInput}
            onChange={(e) => setHexInput(normalizeHexInput(e.target.value))}
            onBlur={() => {
              const c = hexInput.trim();
              if (c === "" || !isValidHex(c))
                setHexInput(savedColor ?? FISCALO_DEFAULT_BRAND_COLOR);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSave) onSave(pendingHex);
            }}
            placeholder={t("settings.brandColor.hexPlaceholder")}
            className={`w-28 font-mono text-sm ${showFormatError ? "border-destructive" : ""}`}
            aria-label={t("settings.brandColor.hexLabel")}
            aria-invalid={showFormatError}
            disabled={disabled}
          />
          {showFormatError && (
            <p className="text-xs text-destructive" role="alert">
              {t("settings.brandColor.hexFormatError")}
            </p>
          )}
        </div>
        <input
          type="color"
          value={effectiveHex}
          onChange={(e) => setHexInput(e.target.value)}
          className="h-10 w-14 cursor-pointer rounded border border-input bg-background"
          aria-label={t("settings.brandColor.title")}
          title={t("settings.brandColor.pickerHint")}
          disabled={disabled}
        />
      </div>

      {/* ---- Suggested from the logo ---- */}
      {logoColors.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs text-muted-foreground">
            {t("settings.brandColor.fromLogo")}
          </div>
          <div className="flex h-10 items-center gap-1.5">
            {logoColors.map((hex) => (
              <button
                key={hex}
                type="button"
                onClick={() => setHexInput(hex)}
                disabled={disabled}
                title={hex}
                aria-label={hex}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-input transition-transform hover:scale-105"
                style={{ background: hex }}
              >
                {effectiveHex.toLowerCase() === hex.toLowerCase() && (
                  <Check className="h-4 w-4 text-white" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- Legibility warning ---- */}
      {lowContrast && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--fx-warn)]/40 bg-[var(--fx-warn)]/10 p-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fx-warn-text)]" />
          <p className="text-xs text-[var(--fx-warn-text)]">
            {t("settings.brandColor.lowContrast", { ratio: contrast?.toFixed(1) })}
          </p>
        </div>
      )}

      {chromeless ? (
        // Dialog footer: right-aligned, primary last, separated from the body.
        <div className="flex items-center justify-end gap-3 border-t pt-4">
          {useDefaultLink}
          {saveButton}
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {saveButton}
          {useDefaultLink}
        </div>
      )}
    </div>
  );
}
